import axios from 'axios';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { PasswordPolicyResponse, SignInResponse } from 'types/auth';
import { apiUrls } from 'app/api.config';
import {
  deleteToken,
  deleteUserGuid,
  isWebClient,
  setToken,
  setUserGuid,
} from 'app/utils/helpers';
import { apiErrorHandler } from 'app/utils/utils';
import { ApiErrorCodeTypes, SECURITY_ERROR } from 'types/enums';
import { config } from 'config';
import {
  formErrorMessageSignIn,
  getAuthPersistenceType,
} from 'app/store/Auth/effects.utils';
import { LocationStateType } from 'types/generic.types';
import {
  FetchApiSettingsResponse,
  FetchBootstrapResponse,
} from 'types/bootstrap';
import { ChangeEmailResponse } from 'types/profile';
import { Link } from 'types/api.types';
import { createAsyncThunk } from 'app/store/utils';
import {
  getChangeEmailLink,
  getChangePasswordLink,
  getPasswordPolicyLink,
  getSignInLink,
  getSignOutLink,
  getVerifyEmailLink,
} from './selectors';
import { addAlertAction } from '../Alerts/slice';
import {
  FetchBootstrapEffectData,
  SignInEffectData,
  SignOutEffectData,
} from './effects.types';

const cleanupUserStorage = (
  dispatch: ThunkDispatch<any, undefined, AnyAction>,
  pathname?: string
) => {
  try {
    deleteToken();
    deleteUserGuid();
    if (pathname !== '/') {
      dispatch({ type: 'auth/logout' });
    }
  } catch (e) {
    // do nothing
  }
};

export const fetchApiKeyWebEffect = createAsyncThunk(
  'auth/fetchApiKey',
  async () => {
    const response = await axios.request<FetchApiSettingsResponse>({
      url: apiUrls.apiKeyWeb,
      method: 'GET',
      withCredentials: false,
    });
    const { items } = response.data.response.settings;
    return items[0].data.apiKey;
  }
);

export const fetchBootstrapEffect = createAsyncThunk(
  'auth/fetchBoostrap',
  async (data: FetchBootstrapEffectData, { getState }) => {
    const state = getState();
    if (!state.auth.apiKeyWeb) {
      throw new Error('Missing apiKeyWeb');
    }
    const response = await axios.get<FetchBootstrapResponse>(
      apiUrls.bootstrap,
      { withCredentials: true }
    );
    const { account, services } = response.data.response;
    if (!config.isElectronBuild && account?.item) {
      setUserGuid(account.item.userGuid);
    }

    const lastLogin = sessionStorage.getItem('lastLogin');
    if (!data?.isSignInOrigin) {
      sessionStorage.removeItem('lastLogin');
    }
    return {
      lastLogin: lastLogin ? JSON.parse(lastLogin) : undefined,
      account: account?.item,
      services,
    };
  }
);

export const signInEffect = createAsyncThunk(
  'auth/signIn',
  async (data: SignInEffectData, { dispatch, getState, rejectWithValue }) => {
    const signInLink = getSignInLink(getState())?.href;
    const isDevWithHttp =
      config.env === 'development' && window.location.protocol === 'http:';
    let lastLogin;
    try {
      if (!signInLink) {
        throw new Error('SignIn link not found');
      }
      const { rememberMe, ...requestData } = data;
      const response = await axios.post<SignInResponse>(
        signInLink,
        {
          ...requestData,
          persistence: getAuthPersistenceType({
            isWebClient,
            isDevWithHttp,
            rememberMe,
          }),
        },
        {
          withCredentials: true,
        }
      );
      const responseData = response?.data.response;
      if (!isWebClient || isDevWithHttp) {
        const authToken =
          responseData?.tokens?.authToken ||
          responseData?.authentication?.item?.tokens.authToken;
        if (authToken) {
          setToken(authToken);
        } else {
          throw new Error('No auth token provided');
        }
      }
      lastLogin = responseData.authentication?.item?.lastLogin || {
        iso: '',
        timestamp: 0,
      };
      if (!config.isElectronBuild && !config.isFeatureBuild) {
        sessionStorage.setItem('lastLogin', JSON.stringify(lastLogin));
      }
    } catch (e: any) {
      const errorMessage = formErrorMessageSignIn(e);
      return rejectWithValue(errorMessage);
    }
    try {
      await dispatch(fetchBootstrapEffect({ isSignInOrigin: true })).unwrap();
    } catch (e: any) {
      if (e?.name !== SECURITY_ERROR) {
        dispatch(addAlertAction({ message: 'Error signing in, try again' }));
      }
      const errorMessage = apiErrorHandler(e.response);
      return rejectWithValue(errorMessage);
    }
    return lastLogin;
  }
);

export const signOutEffect = createAsyncThunk(
  'auth/signOut',
  async (data: SignOutEffectData, { dispatch, getState }) => {
    const { pathname } = data;
    const signOutLink = getSignOutLink(getState())?.href;

    try {
      if (signOutLink) {
        await axios.get(signOutLink, {
          withCredentials: true,
        });
      }
    } finally {
      if (!config.isElectronBuild && !config.isFeatureBuild) {
        sessionStorage.removeItem('lastLogin');
      }

      cleanupUserStorage(
        dispatch as ThunkDispatch<
          Partial<LocationStateType>,
          undefined,
          AnyAction
        >,
        pathname
      );
    }
  }
);

export const changePasswordEffect = createAsyncThunk(
  'auth/changePassword',
  async (
    data: {
      currentPassword: string;
      newPassword: string;
    },
    { dispatch, getState }
  ) => {
    const link = getChangePasswordLink(getState());
    try {
      if (!link) {
        throw new Error('Change password link not found');
      }
      await axios.request({
        url: link.href,
        method: link.method,
        data,
        withCredentials: true,
      });
      dispatch(addAlertAction({ message: 'Password updated' }));
    } catch (e: any) {
      if (e.response.data.error.statusCode === 400) {
        throw e;
      } else {
        dispatch(addAlertAction({ message: apiErrorHandler(e.response) }));
      }
    }
  }
);

export const fetchPasswordPolicyEffect = createAsyncThunk(
  'auth/fetchPasswordPolicy',
  async (_, { getState }) => {
    const link = getPasswordPolicyLink(getState());
    if (!link) {
      throw new Error('Password policy link not found');
    }
    const { data } = await axios.get<PasswordPolicyResponse>(link.href, {
      withCredentials: true,
    });
    return data.response.passwordPolicy.item;
  }
);

export const changeEmailEffect = createAsyncThunk<
  {
    verifyLink: Link | null;
  },
  {
    password: string;
    email: string;
  }
>('auth/changeEmail', async (data, { dispatch, getState, rejectWithValue }) => {
  const link = getChangeEmailLink(getState());
  let verifyLink: Link | null = null;
  try {
    if (!link) {
      throw new Error('Change email link not found');
    }

    const response = await axios.request<ChangeEmailResponse>({
      url: link.href,
      method: link.method,
      data,
      withCredentials: true,
    });
    verifyLink = response.data.response?._links.verifyEmail;
  } catch (e: any) {
    if (
      [
        ApiErrorCodeTypes.BAD_CREDENTIALS,
        ApiErrorCodeTypes.CANNOT_CHANGE_TO_SAME_EMAIL,
      ].includes(e.response.data.error.errorCode) ||
      e.response.data.error.statusReason === ApiErrorCodeTypes.BAD_REQUEST
    ) {
      return rejectWithValue(e);
    }
    if (e.response.data.error.statusCode === 409) {
      dispatch(addAlertAction({ message: e.response.data.error.message }));
    } else {
      dispatch(addAlertAction({ message: apiErrorHandler(e.response) }));
    }
  }
  return {
    verifyLink,
  };
});

export const verifyEmailEffect = createAsyncThunk(
  'auth/verifyEmail',
  async (
    data: {
      code: string;
      newEmail: string;
    },
    { dispatch, getState }
  ) => {
    const link = getVerifyEmailLink(getState());
    try {
      if (!link) {
        throw new Error('Verify email link not found');
      }
      await axios.request({
        url: link.href,
        method: link.method,
        data: { code: data.code },
        withCredentials: true,
      });
      dispatch(addAlertAction({ message: 'Email address updated' }));
    } catch (e: any) {
      const { error } = e.response.data;
      if (
        error.statusReason === ApiErrorCodeTypes.ENTITY_NOT_FOUND ||
        error.statusReason === ApiErrorCodeTypes.BAD_REQUEST
      ) {
        throw e;
      } else {
        dispatch(addAlertAction({ message: apiErrorHandler(e.response) }));
      }
    }
  }
);
